import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import ProspectionMap from "@/components/ProspectionMap";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, formatApiError, formatWeek } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, MapPin, Users } from "lucide-react";

export default function TeamMap() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterWeek, setFilterWeek] = useState("all");
  const [filterUser, setFilterUser] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/prospection/team");
        setEntries(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const weeks = useMemo(
    () => Array.from(new Set(entries.map((e) => e.week_start))).sort().reverse(),
    [entries]
  );

  const teamMembers = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.user_id)) map.set(e.user_id, { user_id: e.user_id, user_name: e.user_name, user_color: e.user_color });
    }
    return Array.from(map.values());
  }, [entries]);

  const filtered = entries.filter((e) => {
    if (filterWeek !== "all" && e.week_start !== filterWeek) return false;
    if (filterUser !== "all" && e.user_id !== filterUser) return false;
    return true;
  });

  const geocoded = filtered.filter((e) => e.lat != null && e.lng != null);

  const perUser = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const cur = map.get(e.user_id) || {
        user_id: e.user_id, user_name: e.user_name, user_color: e.user_color, count: 0,
      };
      cur.count += 1;
      map.set(e.user_id, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [filtered]);

  return (
    <Layout>
      <div className="mb-8" data-testid="team-map">
        <p className="label-eyebrow mb-2">Cartographie équipe</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Carte de l'équipe</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Voyez où vos collègues ont prospecté pour éviter les doublons et mieux couvrir votre secteur.
          Votre couleur : <span className="inline-block h-3 w-3 rounded-full align-middle ring-2 ring-white shadow" style={{ backgroundColor: user?.color }} />
        </p>
      </div>

      <Card className="p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Négociateur</div>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger data-testid="team-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toute l'équipe</SelectItem>
              {teamMembers.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.user_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Semaine</div>
          <Select value={filterWeek} onValueChange={setFilterWeek}>
            <SelectTrigger data-testid="team-filter-week"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les semaines</SelectItem>
              {weeks.map((w) => (<SelectItem key={w} value={w}>{formatWeek(w)}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div className="lg:col-span-3">
          {loading ? (
            <Card className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></Card>
          ) : geocoded.length === 0 ? (
            <Card className="p-12 text-center text-slate-500" data-testid="team-map-empty">
              Aucune adresse prospectée à afficher.<br />
              <span className="text-xs">Ajoutez des adresses depuis votre rapport hebdomadaire — elles apparaîtront ici pour toute l'équipe.</span>
            </Card>
          ) : (
            <ProspectionMap entries={geocoded} height={560} />
          )}
        </div>

        <Card className="p-5 h-fit" data-testid="team-legend">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-primary" />
            <div>
              <p className="label-eyebrow">Légende</p>
              <h3 className="font-display font-bold text-lg">Équipe</h3>
            </div>
          </div>
          {perUser.length === 0 ? (
            <div className="text-sm text-slate-500">Aucune adresse pour ces filtres.</div>
          ) : (
            <div className="space-y-2.5">
              {perUser.map((u) => (
                <div key={u.user_id} className="flex items-center gap-2.5">
                  <span className="h-4 w-4 rounded-full ring-2 ring-white shadow" style={{ backgroundColor: u.user_color }} />
                  <span className={`font-semibold text-sm flex-1 min-w-0 truncate ${u.user_id === user?.id ? "text-primary" : "text-slate-900"}`}>
                    {u.user_name}{u.user_id === user?.id ? " (vous)" : ""}
                  </span>
                  <Badge variant="secondary" className="tabular-nums">{u.count}</Badge>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-border">
            <div className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>
                Astuce : avant de partir prospecter, jetez un œil aux zones déjà couvertes cette semaine.
                Vous maximisez ainsi la couverture géographique de l'équipe.
              </span>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
